import { useNavigate } from 'react-router';
import { mutateConfig } from '../api/mutationConfig';
import { THEMES_KEY, themeUrl, useRefreshThemes, type ProgramTheme, type RemoveThemeResult } from '../api/useProgramThemes';
import { getErrorMessage } from '../lib/apiError';
import { Notice } from './Notice';

const removedNotice = (name: string, status: RemoveThemeResult['status']) =>
  status === 'archived'
    ? `"${name}" was archived because programs use it. Those programs keep it, but it can no longer be edited or given to new programs.`
    : `"${name}" was deleted. No programs were using it.`;

interface ThemeActionsProps {
  theme: ProgramTheme;
  onNotice: (notice: string) => void;
}

export const ThemeActions = ({ theme, onNotice }: ThemeActionsProps) => {
  const navigate = useNavigate();
  const refreshThemes = useRefreshThemes();

  const makeDefault = mutateConfig({ method: 'patch' })<unknown>({ url: `${themeUrl(theme.uid)}/default`, cacheKeys: [THEMES_KEY] });
  const remove = mutateConfig({ method: 'delete' })<RemoveThemeResult>({ url: themeUrl(theme.uid), cacheKeys: [THEMES_KEY] });
  const busy = makeDefault.isPending || remove.isPending;

  const handleMakeDefault = () => {
    const ok = window.confirm(
      `Make "${theme.name}" the default theme?\n\n` +
        'Only programs created from now on will start on it. Existing programs keep the theme they have.'
    );
    if (!ok) return;
    remove.reset();
    makeDefault.mutate(undefined, {
      onSuccess: () => {
        onNotice(`"${theme.name}" is now the default theme. New programs will start on it.`);
        refreshThemes();
      },
    });
  };

  const handleRemove = () => {
    const ok = window.confirm(
      `Archive or delete "${theme.name}"?\n\n` +
        '• If any program uses this theme, it will be archived. Those programs keep it, but it can no longer be edited or given to new programs.\n' +
        '• If no program uses it, it will be deleted permanently.\n\n' +
        "Neither can be undone."
    );
    if (!ok) return;
    makeDefault.reset();
    remove.mutate(undefined, {
      onSuccess: ({ payload }) => {
        refreshThemes({ refetch: false });
        navigate('/themes', { state: { notice: removedNotice(theme.name, payload.status) } });
      },
    });
  };

  const error = makeDefault.error ?? remove.error;

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-5">
          <div className="flex flex-col gap-1">
            <h3 className="text-sm font-semibold text-gray-900">Default theme</h3>
            <p className="text-sm text-gray-500">
              {theme.is_default
                ? 'This is the default theme. Every new program starts on it.'
                : 'New programs start on the default theme. Changing it only affects programs created from now on.'}
            </p>
          </div>
          {!theme.is_default && (
            <button
              type="button"
              onClick={handleMakeDefault}
              disabled={busy}
              className="self-start py-2 px-4 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {makeDefault.isPending ? 'Saving...' : 'Make default'}
            </button>
          )}
        </div>
        <div className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-5">
          <div className="flex flex-col gap-1">
            <h3 className="text-sm font-semibold text-gray-900">Archive or delete</h3>
            <p className="text-sm text-gray-500">
              A theme that programs use is archived: they keep it, but it can't be edited or given to new programs. A
              theme no program uses is deleted.
            </p>
          </div>
          <button
            type="button"
            onClick={handleRemove}
            disabled={busy || theme.is_default}
            aria-describedby={theme.is_default ? 'remove-theme-blocked' : undefined}
            className="self-start py-2 px-4 border border-red-300 text-red-700 text-sm font-medium rounded-lg hover:bg-red-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {remove.isPending ? 'Removing...' : 'Archive or delete'}
          </button>
          {theme.is_default && (
            <p id="remove-theme-blocked" className="text-xs text-gray-500">
              Select another default theme first. The default theme can't be archived or deleted.
            </p>
          )}
        </div>
      </div>
      {error && <Notice tone="error">{getErrorMessage(error)}</Notice>}
    </div>
  );
};
